import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis,
} from 'recharts';
import { useLang, fmt } from '../App';
import { computeSkills } from '../utils/helpers';

/* ══════════════════════════════════════════
   ЦВЕТА МОДОВ
══════════════════════════════════════════ */
const MOD_COLORS_HEX = {
  NM: '#94a3b8', HD: '#fbbf24', DT: '#a78bfa', NC: '#c084fc',
  HR: '#f87171', FL: '#60a5fa', EZ: '#4ade80', HT: '#22d3ee',
  Other: '#6b7280',
};

const TOOLTIP_STYLE = {
  background: 'var(--card2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 12,
  color: 'var(--text)',
};
const TOOLTIP_ITEM_STYLE = { color: 'var(--text)' };
const TOOLTIP_LABEL_STYLE = { color: 'var(--muted)' };

function computeModDist(plays) {
  const passed = plays.filter(p => p.rank !== 'F');
  if (!passed.length) return [];
  const counts = {};
  passed.forEach(p => {
    const mods = (Array.isArray(p.mods) ? p.mods : [])
      .filter(m => !['NF','SD','PF','SO','CL'].includes(m));
    const key = mods.length === 0 ? 'NM' : mods.sort().join('+');
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([name, value]) => ({ name, value, pct: Math.round(value / passed.length * 100) }));
}

const MAP_PATTERNS = [
  { key: 'Jumps',   labelRu: 'Прыжки',   color: '#f87171', re: /jump|aim|triangle|farmable|farm|hdhr|hddt/i },
  { key: 'Streams', labelRu: 'Стримы',   color: '#a78bfa', re: /stream|burst|vibro|deathstream|speed|stamina/i },
  { key: 'Tech',    labelRu: 'Техника',  color: '#fbbf24', re: /tech|alt|complex|cross|sv|slider|reading|gimmick/i },
  { key: 'Finger',  labelRu: 'Пальцы',  color: '#60a5fa', re: /finger|control|tapspeed|alt/i },
  { key: 'Farm',    labelRu: 'Фарм',    color: '#4ade80', re: /easy|light|practice|beginner|normal/i },
];

function computeMapTypes(plays) {
  const passed = plays.filter(p => p.rank !== 'F');
  if (!passed.length) return [];
  const counts = { Other: 0 };
  MAP_PATTERNS.forEach(t => { counts[t.key] = 0; });
  passed.forEach(p => {
    const text = `${p.diff || ''} ${p.tags || ''}`;
    let matched = false;
    for (const pat of MAP_PATTERNS) {
      if (pat.re.test(text)) { counts[pat.key]++; matched = true; break; }
    }
    if (!matched) counts['Other']++;
  });
  return [...MAP_PATTERNS.map(t => ({ name: t.key, nameRu: t.labelRu, value: counts[t.key], color: t.color })),
    { name: 'Other', nameRu: 'Другое', value: counts['Other'], color: '#6b7280' }]
    .filter(t => t.value > 0).sort((a, b) => b.value - a.value);
}

const GENRE_PATTERNS = [
  { key: 'Anime',      labelRu: 'Аниме',      color: '#f472b6', re: /anime|vocaloid|touhou|hatsune|miku|opening|ending/i },
  { key: 'Electronic', labelRu: 'Электроника', color: '#a78bfa', re: /electronic|edm|dubstep|dnb|drum|bass|house|techno|camellia|xi|lapix|kobaryo/i },
  { key: 'Rock/Metal', labelRu: 'Рок/Метал',   color: '#f87171', re: /rock|metal|punk|hardcore|guitar/i },
  { key: 'Pop',        labelRu: 'Поп',          color: '#fb923c', re: /pop|jpop|kpop|idol|love/i },
  { key: 'Game OST',   labelRu: 'Игры',         color: '#4ade80', re: /game|ost|cytus|maimai|toby fox/i },
  { key: 'Classical',  labelRu: 'Классика',     color: '#60a5fa', re: /classical|piano|violin|orchestra/i },
];

function computeGenres(plays) {
  const passed = plays.filter(p => p.rank !== 'F');
  if (!passed.length) return [];
  const counts = { Other: 0 };
  GENRE_PATTERNS.forEach(g => { counts[g.key] = 0; });
  passed.forEach(p => {
    const text = `${p.map || ''} ${p.tags || ''}`;
    let matched = false;
    for (const g of GENRE_PATTERNS) {
      if (g.re.test(text)) { counts[g.key]++; matched = true; break; }
    }
    if (!matched) counts['Other']++;
  });
  return [...GENRE_PATTERNS.map(g => ({ name: g.key, nameRu: g.labelRu, value: counts[g.key], color: g.color })),
    { name: 'Other', nameRu: 'Другое', value: counts['Other'], color: '#6b7280' }]
    .filter(g => g.value > 0).sort((a, b) => b.value - a.value).slice(0, 6);
}

function computeQuickStats(user) {
  const recent = user?.recentActivity || [];
  const passed = recent.filter(p => p.rank !== 'F');
  const best   = user?.bestPlays || [];

  const ppPerPlay = best.length
    ? Math.round(best.reduce((s, p) => s + (p.pp || 0), 0) / best.length) : 0;
  const avgMisses = passed.length
    ? +(passed.reduce((s, p) => s + (p.miss_count || 0), 0) / passed.length).toFixed(1) : 0;
  const avgHits = (() => {
    // Сначала считаем из recent plays
    const withHits = passed.filter(p => p.hit_count > 0);
    if (withHits.length > 0)
      return Math.round(withHits.reduce((s, p) => s + p.hit_count, 0) / withHits.length);
    // Фолбэк: из общей статистики профиля
    if (user?.total_hits && user?.play_count)
      return Math.round(user.total_hits / user.play_count);
    return 0;
  })();

  const totalGrades = Object.values(user?.grades || {}).reduce((s, v) => s + v, 0);
  const goodGrades  = (user?.grades?.SSH || 0) + (user?.grades?.SS || 0) + (user?.grades?.S || 0) + (user?.grades?.SH || 0);
  const consistency = totalGrades > 0 ? Math.round(goodGrades / totalGrades * 100) : 0;

  const avgStars = passed.length
    ? +(passed.reduce((s, p) => s + (p.stars || 0), 0) / passed.length).toFixed(2)
    : (user?.topPlay?.stars || 0);  // фолбэк — сложность лучшего скора

  return { ppPerPlay, avgMisses, avgHits, consistency, avgStars };
}

/* ══════════════════════════════════════════
   КОМПОНЕНТЫ
══════════════════════════════════════════ */

const StatCard = ({ icon, label, value, sub, color, delay = 0 }) => (
  <div className="card fu" style={{ padding: '16px 18px', animationDelay: `${delay}s` }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</span>
    </div>
    <div className="mono" style={{ fontSize: 26, fontWeight: 800, color: color || 'var(--a)', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 5 }}>{sub}</div>}
  </div>
);

const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, pct }) => {
  if (pct < 6) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700}>{pct}%</text>;
};

const ConsistencyMeter = ({ value, lang }) => {
  const color = value >= 80 ? '#68d891' : value >= 55 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="mono" style={{ fontSize: 42, fontWeight: 800, color, lineHeight: 1 }}>{value}%</span>
      </div>
      <div style={{ height: 9, background: 'var(--bg2)', borderRadius: 20, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, background: `linear-gradient(90deg,${color}88,${color})`, borderRadius: 20, transition: 'width 1.4s cubic-bezier(.22,.61,.36,1)' }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
        {lang === 'ru' ? 'S/SS/SSH от всех грейдов' : 'S/SS/SSH out of all ranked grades'}
      </span>
    </div>
  );
};

const MedalHex = ({ achievement_id, achieved_at, lang }) => {
  const date = achieved_at
    ? new Date(achieved_at).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', { month: 'short', day: 'numeric' })
    : '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}
      title={`Medal #${achievement_id}${date ? ' · ' + date : ''}`}>
      <div style={{
        width: 60, height: 60,
        clipPath: 'polygon(50% 0%,93% 25%,93% 75%,50% 100%,7% 75%,7% 25%)',
        background: 'var(--dim)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', position: 'relative', border: '1px solid var(--border)',
      }}>
        <img
          src={`https://s.ppy.sh/images/medals-client/${achievement_id}@2x.png`}
          alt={`medal`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
        <div style={{ display: 'none', position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🏅</div>
      </div>
      {date && <span style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center' }}>{date}</span>}
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN
══════════════════════════════════════════ */
const Statistics = ({ user, onRefresh, isRefreshing = false }) => {
  const { lang } = useLang();

  const recent = user?.recentActivity || [];
  const passed = recent.filter(p => p.rank !== 'F');

  const skills   = useMemo(() => computeSkills(user)     || [], [user]);
  const modDist  = useMemo(() => computeModDist(recent),        [recent]);
  const mapTypes = useMemo(() => computeMapTypes(recent),       [recent]);
  const genres   = useMemo(() => computeGenres(recent),         [recent]);
  const qs       = useMemo(() => computeQuickStats(user),       [user]);
  const medals   = user?.user_achievements || [];

  const getModColor = (name) => MOD_COLORS_HEX[name.split('+')[0]] || MOD_COLORS_HEX.Other;

  if (!user) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
      {lang === 'ru' ? 'Загрузка...' : 'Loading...'}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* ЗАГОЛОВОК */}
      <div className="fu" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h2 className="syne" style={{ fontSize: 22, fontWeight: 800 }}>
            {lang === 'ru' ? 'Статистика' : 'Statistics'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: 'var(--muted)', padding: '2px 10px', borderRadius: 20, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
              📊 {lang === 'ru'
                ? `Данные из последних ${passed.length} игр (recent plays)`
                : `Based on last ${passed.length} plays (recent activity)`}
            </span>
            {user?.fetched_at && (
              <span style={{ fontSize: 10, color: 'var(--muted)', opacity: 0.5 }}>
                {lang === 'ru' ? 'обновлено в' : 'updated at'}{' '}
                {new Date(user.fetched_at).toLocaleTimeString(lang === 'ru' ? 'ru-RU' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="btn-ghost"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 16px', borderRadius: 20, fontSize: 12, fontFamily: 'inherit',
              opacity: isRefreshing ? 0.5 : 1,
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
            }}
          >
            <span style={{ display: 'inline-block', animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }}>↻</span>
            {isRefreshing ? (lang === 'ru' ? 'Загрузка...' : 'Loading...') : (lang === 'ru' ? 'Обновить' : 'Refresh')}
          </button>
        )}
      </div>

      {/* РЯД 1: 4 быстрые метрики */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
        <StatCard icon="◆" label={lang === 'ru' ? 'PP за игру' : 'PP / Play'}
          value={`${qs.ppPerPlay}pp`} color="var(--a)" delay={0}
          sub={lang === 'ru' ? 'среднее топ-10' : 'avg top-10 plays'} />
        <StatCard icon="✖" label={lang === 'ru' ? 'Ср. миссы' : 'Avg Misses'}
          value={qs.avgMisses} color="#f87171" delay={0.05}
          sub={lang === 'ru' ? 'за игру' : 'per play'} />
        <StatCard icon="◎" label={lang === 'ru' ? 'Ср. попадания' : 'Avg Hits'}
          value={fmt(qs.avgHits)} color="#68d891" delay={0.1}
          sub={lang === 'ru' ? 'за игру' : 'per play'} />
        <StatCard icon="✦" label={lang === 'ru' ? 'Ср. сложность' : 'Avg Stars'}
          value={`${qs.avgStars}★`} color="#ffb6d0" delay={0.15}
          sub={lang === 'ru' ? 'пройденных карт' : 'passed maps'} />
      </div>

      {/* РЯД 2: Skill Breakdown | Mod Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>

        <div className="card fu d2" style={{ padding: 22 }}>
          <div style={{ fontWeight: 700, marginBottom: 18, fontSize: 14 }}>
            <span className="accent-text">◉</span> {lang === 'ru' ? 'Навыки' : 'Skill Breakdown'}
          </div>
          {skills.map(s => (
            <div key={s.skill} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 78, fontSize: 11, color: 'var(--muted)' }}>{lang === 'ru' ? s.skillRu : s.skill}</div>
              <div className="pb-wrap" style={{ flex: 1, height: 7 }}>
                <div className="pb" style={{ width: `${s.value}%`, height: '100%' }} />
              </div>
              <span className="mono" style={{ width: 24, fontSize: 13, fontWeight: 700, textAlign: 'right' }}>{s.value}</span>
            </div>
          ))}
        </div>

        <div className="card fu d2" style={{ padding: 22 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>
            <span className="accent-text">◈</span> {lang === 'ru' ? 'Распределение модов' : 'Mod Distribution'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
            {lang === 'ru' ? 'Из пройденных игр' : 'From passed plays'}
          </div>
          {modDist.length === 0
            ? <div style={{ color: 'var(--muted)', fontSize: 12 }}>{lang === 'ru' ? 'Нет данных' : 'No data'}</div>
            : <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flexShrink: 0 }}>
                  <PieChart width={150} height={150}>
                    <Pie data={modDist} dataKey="value" cx={75} cy={75}
                      outerRadius={68} innerRadius={32} paddingAngle={2}
                      labelLine={false} label={renderPieLabel}>
                      {modDist.map((e, i) => (
                        <Cell key={i} fill={getModColor(e.name)} stroke="var(--card)" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      itemStyle={TOOLTIP_ITEM_STYLE}
                      formatter={(v, n) => [`${v} plays`, n]} />
                  </PieChart>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {modDist.map((m, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 9, height: 9, borderRadius: 3, flexShrink: 0, background: getModColor(m.name) }} />
                      <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: getModColor(m.name) }}>{m.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 'auto' }}>{m.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
          }
        </div>
      </div>

      {/* РЯД 3: Map Types | Music Genres */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>

        <div className="card fu d3" style={{ padding: 22 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>
            <span className="accent-text">▶</span> {lang === 'ru' ? 'Типы карт' : 'Map Types'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14 }}>
            {lang === 'ru' ? 'По диффам и тегам' : 'From diff names & tags'}
          </div>
          {mapTypes.length === 0
            ? <div style={{ color: 'var(--muted)', fontSize: 12 }}>{lang === 'ru' ? 'Нет данных' : 'No data'}</div>
            : <ResponsiveContainer width="100%" height={170}>
                <BarChart data={mapTypes.map(t => ({ ...t, label: lang === 'ru' ? t.nameRu : t.name }))}
                  layout="vertical" barSize={22}>
                  <XAxis type="number" tick={{ fill: 'var(--muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="label" tick={{ fill: 'var(--muted)', fontSize: 11 }}
                    axisLine={false} tickLine={false} width={68} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    itemStyle={TOOLTIP_ITEM_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    formatter={v => [v, lang === 'ru' ? 'игр' : 'plays']} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {mapTypes.map((t, i) => <Cell key={i} fill={t.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
          }
        </div>

        <div className="card fu d3" style={{ padding: 22 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>
            <span className="accent-text">♪</span> {lang === 'ru' ? 'Музыкальные жанры' : 'Music Genres'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16 }}>
            {lang === 'ru' ? 'По названиям и тегам карт' : 'From map titles & tags'}
          </div>
          {genres.length === 0
            ? <div style={{ color: 'var(--muted)', fontSize: 12 }}>{lang === 'ru' ? 'Нет данных' : 'No data'}</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                {genres.map((g, i) => {
                  const maxVal = genres[0].value;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 78, fontSize: 11, color: 'var(--muted)', flexShrink: 0 }}>
                        {lang === 'ru' ? g.nameRu : g.name}
                      </div>
                      <div style={{ flex: 1, height: 7, background: 'var(--bg2)', borderRadius: 20, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${g.value / maxVal * 100}%`, background: g.color,
                          borderRadius: 20, boxShadow: `0 0 8px ${g.color}55`,
                          transition: 'width 1.2s cubic-bezier(.22,.61,.36,1)' }} />
                      </div>
                      <span className="mono" style={{ fontSize: 11, color: g.color, fontWeight: 700, width: 30, textAlign: 'right' }}>
                        {Math.round(g.value / (passed.length || 1) * 100)}%
                      </span>
                    </div>
                  );
                })}
              </div>
          }
        </div>
      </div>

      {/* РЯД 4: Consistency Score + Grades */}
      <div className="card fu d4" style={{ padding: 22 }}>
        <div style={{ fontWeight: 700, marginBottom: 18, fontSize: 14 }}>
          <span className="accent-text">◆</span> {lang === 'ru' ? 'Стабильность' : 'Grades'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'start' }}>
          <ConsistencyMeter value={qs.consistency} lang={lang} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {Object.entries(user?.grades || {}).map(([g, cnt]) => (
              <div key={g} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className={`rank-badge rank-${g}`} style={{ width: 36, textAlign: 'center', flexShrink: 0, fontSize: 10 }}>{g}</span>
                <div className="pb-wrap" style={{ flex: 1 }}>
                  <div className="pb" style={{ width: `${Math.min(100, cnt / 7)}%` }} />
                </div>
                <span className="mono" style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>{fmt(cnt)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* РЯД 5: Medals который я снес*/}
    </div>
  );
};

export default Statistics;